import React from 'react';
import { array, bool, func, number, shape, string } from 'prop-types';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import routeConfiguration from '../../routeConfiguration';
import { withViewport } from '../../util/contextHelpers';
import { parse, stringify } from '../../util/urlHelpers';
import { pathByRouteName } from '../../util/routes';
import { pathForLocale } from '../../util/locale';
import { propTypes } from '../../util/types';
import {
  LimitedAccessBanner,
  TopbarDesktop,
  TopbarMobileMenu,
  LanguageModal,
  NewsletterPromptModal,
} from '../../components';

import TopbarNotice from './TopbarNotice';
import css from './Topbar.module.css';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const MOBILE_MENU_PARAM = 'mobilemenu';
const isBetaMode = process.env.REACT_APP_BETA_MODE === 'true';

// The phone menu is open while the URL has ?mobilemenu=open (the template's pattern), so the
// browser's back button closes it.
const withMobileMenuParam = (location, isOpen) => {
  const { pathname, search } = location;
  const others = pickBy(parse(search), (v, k) => k !== MOBILE_MENU_PARAM);
  const params = isOpen ? { [MOBILE_MENU_PARAM]: 'open', ...others } : others;
  const query = stringify(params);
  return `${pathname}${query ? `?${query}` : ''}`;
};

const GenericError = ({ show }) => (
  <div className={classNames(css.genericError, { [css.genericErrorVisible]: show })}>
    <p className={css.genericErrorText}>
      <FormattedMessage id="Topbar.genericError" />
    </p>
  </div>
);

/**
 * The public top bar: the desktop bar from 768px up, the phone bar and its menu below, plus the
 * banners above them (limited access, beta notice) and the language modal.
 */
const TopbarComponent = props => {
  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    contentClassName,
    logoClassName,
    isAuthenticated,
    authScopes,
    currentUser,
    currentPage,
    viewport,
    location,
    history,
    languageModalOpen,
    onToggleLanguageModal,
    onChangeLanguage,
    onManageDisableScrolling,
    onLogout,
    showGenericError,
    logoFormat,
    unreadCount,
  } = props;
  const intl = useIntl();

  const handleLogout = () =>
    onLogout().then(() => {
      const path = pathByRouteName(
        isBetaMode ? 'CommunityPage' : 'LandingPage',
        routeConfiguration()
      );
      window.location = pathForLocale(path, intl.locale);
    });

  const setMobileMenuOpen = isOpen =>
    history.push(withMobileMenuParam(location, isOpen), location.state);

  const isMobileLayout = viewport.width < MAX_MOBILE_SCREEN_WIDTH;
  const isMobileMenuOpen = isMobileLayout && parse(location.search).mobilemenu === 'open';

  return (
    <div className={classNames(rootClassName || css.root, className)}>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={currentPage}
      />
      <TopbarNotice />
      <div className={css.mobile}>
        <TopbarMobileMenu
          rootClassName={mobileRootClassName}
          className={mobileClassName}
          isAuthenticated={isAuthenticated}
          isBetaMode={isBetaMode}
          currentUser={currentUser}
          unreadCount={unreadCount}
          isOpen={isMobileMenuOpen}
          onOpen={() => setMobileMenuOpen(true)}
          onClose={() => setMobileMenuOpen(false)}
          onLogout={handleLogout}
          onManageDisableScrolling={onManageDisableScrolling}
        />
      </div>
      <div className={css.desktop}>
        <TopbarDesktop
          className={desktopClassName}
          contentClassName={contentClassName}
          logoClassName={logoClassName}
          currentUser={currentUser}
          currentPage={currentPage}
          isAuthenticated={isAuthenticated}
          isBetaMode={isBetaMode}
          logoFormat={logoFormat}
          unreadCount={unreadCount}
          onLogout={handleLogout}
          onOpenLanguageModal={() => onToggleLanguageModal(true)}
        />
      </div>
      <LanguageModal
        id="LanguageModal"
        isOpen={languageModalOpen}
        onCloseModal={() => onToggleLanguageModal(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        onChangeLanguage={onChangeLanguage}
      />
      {isBetaMode ? <NewsletterPromptModal /> : null}
      <GenericError show={showGenericError} />
    </div>
  );
};

TopbarComponent.defaultProps = {
  className: null,
  rootClassName: null,
  desktopClassName: null,
  mobileRootClassName: null,
  mobileClassName: null,
  contentClassName: null,
  logoClassName: null,
  currentUser: null,
  currentPage: null,
  authScopes: [],
  languageModalOpen: false,
  logoFormat: null,
  unreadCount: 0,
};

TopbarComponent.propTypes = {
  className: string,
  rootClassName: string,
  desktopClassName: string,
  mobileRootClassName: string,
  mobileClassName: string,
  contentClassName: string,
  logoClassName: string,
  isAuthenticated: bool.isRequired,
  authScopes: array,
  currentUser: propTypes.currentUser,
  currentPage: string,
  languageModalOpen: bool,
  onLogout: func.isRequired,
  onToggleLanguageModal: func.isRequired,
  onChangeLanguage: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  showGenericError: bool.isRequired,
  logoFormat: string,
  unreadCount: number,
  history: shape({ push: func.isRequired }).isRequired,
  location: shape({ search: string.isRequired }).isRequired,
  viewport: shape({ width: number.isRequired, height: number.isRequired }).isRequired,
};

const Topbar = withViewport(TopbarComponent);

Topbar.displayName = 'Topbar';

export default Topbar;
